
import React, { useEffect, useState } from "react";
import api from "../../utils/api";
import TaskForm from "./TaskForm";
import TaskDetailsDialog from "./TaskDetailsDialog";
import {
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  CalendarIcon,
  UserIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";

// Reusing the same statusConfig as in TaskDetailsDialog for consistency
const statusConfig = {
  Pending: { icon: <ClockIcon className="w-4 h-4" />, color: "text-amber-600", bg: "bg-amber-50 border-amber-200" },
  "In Progress": { icon: <ClockIcon className="w-4 h-4" />, color: "text-blue-600", bg: "bg-blue-50 border-blue-200" },
  "Needs Approval": { icon: <ExclamationTriangleIcon className="w-4 h-4" />, color: "text-purple-600", bg: "bg-purple-50 border-purple-200" },
  Completed: { icon: <CheckCircleIcon className="w-4 h-4" />, color: "text-green-600", bg: "bg-green-50 border-green-200" },
  Approved: { icon: <CheckCircleIcon className="w-4 h-4" />, color: "text-green-600", bg: "bg-green-50 border-green-200" },
  Rejected: { icon: <ExclamationTriangleIcon className="w-4 h-4" />, color: "text-red-600", bg: "bg-red-50 border-red-200" },
};

const columns = [
  { id: "todo", title: "To Do", statuses: ["Pending"] },
  { id: "inprogress", title: "In Progress", statuses: ["In Progress"] },
  { id: "waiting", title: "Waiting/Approval", statuses: ["Needs Approval"] },
  { id: "completed", title: "Completed", statuses: ["Completed", "Approved"] },
];

const priorityBorder = {
  Urgent: "border-l-red-500",
  High: "border-l-orange-500",
  Medium: "border-l-blue-500",
  Low: "border-l-green-500",
};

export default function TaskList() {
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [detailedTask, setDetailedTask] = useState(null);
  const [view, setView] = useState("kanban");

  const [filters, setFilters] = useState({
    type: "All Tasks",
    company: "All",
    module: "All",
    status: "All",
    priority: "All",
  });

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isManager = ["SuperAdmin", "Admin", "TeamLead"].includes(user.role || "");

  const loadTasks = async () => {
    try {
      const endpoint = isManager ? "/tasks?team=true" : "/tasks?mine=true";
      const { data } = await api.get(endpoint);
      setTasks(data.tasks || []);
    } catch (err) {
      console.error("Failed to load tasks:", err);
    }
  };

  useEffect(() => {
    loadTasks();
  }, []);

  useEffect(() => {
    let result = tasks;

    if (filters.type === "My Tasks") {
      result = result.filter((t) => t.assignee_id === user.id || t.assigned_to === user.id);
    }

    if (filters.status !== "All") result = result.filter((t) => t.status === filters.status);
    if (filters.priority !== "All") result = result.filter((t) => t.priority === filters.priority);

    setFilteredTasks(result);
  }, [tasks, filters, user.id]);

  const getColumnTasks = (column) => {
    return filteredTasks.filter((t) => column.statuses.includes(t.status));
  };

  // const handleCompleteTask = async (taskId, e) => {
  //   e.stopPropagation();
  //   try {
  //     await api.post(`/tasks/${taskId}/complete`);
  //     loadTasks();
  //     alert("Task marked as complete! Awaiting approval.");
  //   } catch (err) {
  //     alert("Failed to complete task");
  //   }
  // };
const handleCompleteTask = async (taskId, proofData) => {
  try {
    await api.post(`/tasks/${taskId}/complete`, proofData, {
      headers: { "Content-Type": "multipart/form-data" }
    });
    loadTasks();
  } catch (err) {
    alert("Failed to complete task");
  }
};
  const formatDueDate = (date) => {
    if (!date) return "No due date";
    return new Date(date).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };
const openTaskDetails = async (task) => {
  setSelectedTask(task); // for immediate UI feedback
  try {
    const { data } = await api.get(`/tasks/${task.id}`);
    setDetailedTask(data.task); // this has attachments!
  } catch (err) {
    console.error("Failed to load task details", err);
    setDetailedTask(task); // fallback
  }
};
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Top Bar */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Task</h1>

            <select
              value={filters.type}
              onChange={(e) => setFilters({ ...filters, type: e.target.value })}
              className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium"
            >
              <option>All Tasks</option>
              <option>My Tasks</option>
              {isManager && <option>Team Tasks</option>}
            </select>

            <select
              value={filters.priority}
              onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
              className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium"
            >
              <option value="All">All Priorities</option>
              <option>Urgent</option>
              <option>High</option>
              <option>Medium</option>
              <option>Low</option>
            </select>

            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium"
            >
              <option value="All">All Statuses</option>
              <option>Pending</option>
              <option>In Progress</option>
              <option>Needs Approval</option>
              <option>Completed</option>
              <option>Approved</option>
            </select>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => setOpenForm(true)}
              className="px-5 py-2.5 bg-slate-800 hover:bg-slate-800 text-white font-medium rounded-lg flex items-center gap-2 transition"
            >
              <PlusIcon className="w-5 h-5" />
              Create Task
            </button>

            <button
              onClick={() => setView(view === "kanban" ? "table" : "kanban")}
              className="px-5 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg font-medium hover:bg-gray-100 dark:hover:bg-gray-800 transition"
            >
              {view === "kanban" ? "List View" : "View"}
            </button>
          </div>
        </div>

        {/* Kanban View */}
        {view === "kanban" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {columns.map((column) => {
              const columnTasks = getColumnTasks(column);
              return (
                <div
                  key={column.id}
                  className="bg-gray-100 dark:bg-gray-800 rounded-2xl p-5 min-h-[600px] flex flex-col"
                >
                  <h3 className="font-bold text-lg mb-4 text-gray-900 dark:text-white flex items-center justify-between">
                    {column.title}
                    <span className="text-sm font-normal bg-gray-200 dark:bg-gray-700 px-3 py-1 rounded-full">
                      {columnTasks.length}
                    </span>
                  </h3>

                  <div className="space-y-4 flex-1 overflow-y-auto">
                    {columnTasks.length === 0 ? (
                      <p className="text-center text-gray-500 dark:text-gray-400 py-8">
                        No tasks here
                      </p>
                    ) : (
                      columnTasks.map((task) => (
                        // <div
                        //   key={task.id}
                        //   onClick={() => setSelectedTask(task)}
                        //   className={`bg-white dark:bg-gray-700 rounded-xl shadow-md border border-gray-200 dark:border-gray-600 hover:shadow-lg cursor-pointer transition-all p-6 border-l-8 ${priorityBorder[task.priority] || "border-l-gray-300"}`}
                        // >
                        <div
                        key={task.id}
                        onClick={() => openTaskDetails(task)} // Open dialog on card click
                        className={`bg-white dark:bg-gray-700 rounded-xl shadow-md border border-gray-200 dark:border-gray-600 hover:shadow-lg cursor-pointer transition-all p-6 border-l-8 ${
                          priorityBorder[task.priority] || "border-l-gray-300"
                        }`}
                      >

                        {/* ADD THESE 4 LINES BELOW — TEMPORARY DEBUG */}
    {console.log("User team_id:", user.team_id)}
    {console.log("Task assigned_team_id:", task.assigned_team_id)}
    {console.log("Is team task:", task.is_team_task)}
    {console.log("Show complete button?", 
      (task.assigned_to === user.id || 
       (task.assigned_team_id && user.team_id === task.assigned_team_id))
    )}
                          {/* Task Title */}
                          <h4 className="font-semibold text-lg text-gray-900 dark:text-white mb-5">
                            {task.title}
                          </h4>

                          {/* Assigned To */}
                          {/* <div className="mb-5">
                            <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Assigned To</div>
                            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                              <UserIcon className="w-4 h-4" />
                            <span>
                              {task.assignee_name || task.assigned_to_name || task.assigned_to || "Unassigned"}
                            </span>
                          </div>
                          </div> */}
          <div className="mb-5">
  <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Assigned To</div>
  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
    {task.is_team_task ? (
      <>
        <UserGroupIcon className="w-5 h-5 text-orange-600" />
        <span className="font-medium text-orange-700">
          Team: {task.assigned_team_name}
        </span>
      </>
    ) : (
      <>
        <UserIcon className="w-4 h-4" />
        <span>{task.assignee_name || "Unassigned"}</span>
      </>
    )}
  </div>
</div>

                          {/* Due Date */}
                          <div className="mb-6">
                            <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Due Date</div>
                            <div className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                              <CalendarIcon className="w-5 h-5 text-gray-500" />
                              {formatDueDate(task.due_date)}
                            </div>
                          </div>

                          {/* Status + Action */}
                          <div className="flex justify-between items-center">
                            <div
                              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium ${statusConfig[task.status]?.bg || "bg-gray-50 border-gray-200"} ${statusConfig[task.status]?.color || "text-gray-700"}`}
                            >
                              {statusConfig[task.status]?.icon}
                              <span>{task.status}</span>
                            </div>

                            {/* {["Pending", "In Progress"].includes(task.status) && (
                       
<button
  onClick={(e) => handleCompleteTask(task.id, e)}
  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-green-700 hover:text-green-900 bg-green-50 hover:bg-green-100 rounded-lg transition"
>
  <CheckCircleIcon className="w-4 h-4" />
  Complete
</button>
                            )} */}
                            {/* {["Pending", "In Progress"].includes(task.status) && task.assigned_to === user.id && (
  <button
    onClick={(e) => {
      e.stopPropagation(); // Prevent opening normal details
      openTaskDetails(task); // Opens panel in view mode — we'll fix mode next
    }}
    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-green-700 hover:text-green-900 bg-green-50 hover:bg-green-100 rounded-lg transition"
  >
    <CheckCircleIcon className="w-4 h-4" />
    Complete
  </button>
)} */}
{/* NEW: Show Complete button if task is Pending/In Progress AND user is either individual assignee OR member of assigned team */}
{["Pending", "In Progress"].includes(task.status) &&
  (task.assigned_to === user.id || (task.assigned_team_id && user.team_id === task.assigned_team_id)) && (
  <button
    onClick={(e) => {
      e.stopPropagation();
      openTaskDetails(task);
    }}
    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-green-700 hover:text-green-900 bg-green-50 hover:bg-green-100 rounded-lg transition"
  >
    <CheckCircleIcon className="w-4 h-4" />
    Complete
  </button>
)}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* Table View (unchanged - you can update it later if needed) */
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Task</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Assignee</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                    <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredTasks.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                        No tasks found
                      </td>
                    </tr>
                  ) : (
                    filteredTasks.map((task) => (
                     <tr
  key={task.id}
  onClick={() => openTaskDetails(task)}  // ← CORRECT — same as Kanban
  className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
>
                        <td className="px-6 py-4">
                          <div className="font-medium text-gray-900 dark:text-white">{task.title}</div>
                          <div className="text-sm text-gray-500">{task.task_uid}</div>
                        </td>
                        <td className="px-6 py-4 text-gray-700 dark:text-gray-300">
                          {task.assignee_name || "Unassigned"}
                        </td>
                        <td className="px-6 py-4">
                          <span className="font-medium">{task.priority || "Medium"}</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusConfig[task.status]?.bg || "bg-gray-100"} ${statusConfig[task.status]?.color || "text-gray-700"}`}>
                            {task.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-gray-700 dark:text-gray-300">
                          {formatDueDate(task.due_date)}
                        </td>
                        <td className="px-6 py-4 text-center">
                          {/* {["Pending", "In Progress"].includes(task.status) && (
                            <button
                              onClick={(e) => handleCompleteTask(task.id, e)}
                              className="text-green-600 hover:text-green-800 font-medium text-sm"
                            >
                              Mark Complete
                            </button>
                          )} */}
                          {/* {["Pending", "In Progress"].includes(task.status) && task.assigned_to === user.id && (
  <button
    onClick={(e) => {
      e.stopPropagation();
      openTaskDetails(task);
    }}
    className="text-green-600 hover:text-green-800 font-medium text-sm"
  >
    Mark Complete
  </button>
)} */}
{/* NEW: Show Complete button if task is Pending/In Progress AND user is either individual assignee OR member of assigned team */}
{["Pending", "In Progress"].includes(task.status) &&
  (task.assigned_to === user.id || (task.assigned_team_id && user.team_id === task.assigned_team_id)) && (
  <button
    onClick={(e) => {
      e.stopPropagation();
      openTaskDetails(task);
    }}
    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-green-700 hover:text-green-900 bg-green-50 hover:bg-green-100 rounded-lg transition"
  >
    <CheckCircleIcon className="w-4 h-4" />
      Mark Complete
  </button>
)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Modals */}
        <TaskForm
          open={openForm}
          onClose={(success) => {
            setOpenForm(false);
            if (success) loadTasks();
          }}
        />

        <TaskDetailsDialog
          task={detailedTask ||selectedTask}
          open={!!selectedTask}
          onClose={() => {setSelectedTask(null);
            setDetailedTask(null);}}
          // currentUserId={user.id}
          user={user}
          isManager={isManager}
  //         mode={
  //   selectedTask &&
  //   ["Pending", "In Progress"].includes(selectedTask.status) &&
  //   selectedTask.assigned_to === user.id
  //     ? "complete"
  //     : "view"
  // }
  mode={
  selectedTask &&
  ["Pending", "In Progress"].includes(selectedTask.status) &&
  (selectedTask.assigned_to === user.id || 
   (selectedTask.assigned_team_id && user.team_id === selectedTask.assigned_team_id))
    ? "complete"
    : "view"
}
          onApprove={async (approved, comment) => {
            try {
              await api.post(`/tasks/${selectedTask.id}/approve`, {
                action: approved ? "approve" : "reject",
                comments: comment,
              });
              loadTasks();
              setSelectedTask(null);
              setDetailedTask(null);
            } catch {
              alert("Approval failed");
            }
          }}
          onComplete={async (taskId, proofData = new FormData()) => {
    try {
      await api.post(`/tasks/${taskId}/complete`, proofData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      loadTasks();
      alert("Task completed successfully!");
    } catch (err) {
      alert("Failed to complete task");
    }
  }}
        />
      </div>
    </div>
  );
}